import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Calendar,
  MapPin,
  Users,
  Check,
  Sparkles,
  Ticket,
  Rocket,
  Beaker,
  Award,
  Globe,
  PlayCircle,
  Radio,
} from "lucide-react";
import { fetcher, http } from "@/lib/api";

export default function PLIEvents() {
  const { data } = useSWR("/pli-events", fetcher);
  const events = data || [];
  // Featured edition: PLI 3.0 (richest data), shown as completed
  const upcoming = events.find((e) => e.id === "pli-3") || events[events.length - 1];

  return (
    <div className="pt-32 pb-24">
      {/* ─── HERO ─── */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-8">
            <div className="eyebrow mb-5">PLI · Private Label Innovation</div>
            <h1 className="font-editorial text-5xl md:text-8xl text-[#1D3A2F] leading-[0.95]">
              Where beauty
              <br />
              founders gather.
            </h1>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="text-lg text-[#222222]/70 leading-relaxed">
              India's flagship convention for D2C beauty brands, private-label
              founders and global buyers. Three editions. One movement.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-24">
          <Metric k="3" v="Editions" />
          <Metric k="1720+" v="Total Delegates" />
          <Metric k="45+" v="Speakers" />
          <Metric k="30+" v="Cities Represented" />
        </div>
      </section>

      {/* ─── FIRST EVER IN INDIA · Hero Banner ─── */}
      {upcoming && (
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 -mt-8 mb-24">
        <div className="rounded-3xl bg-gradient-to-br from-[#1D3A2F] via-[#2A4E3F] to-[#0F251C] text-[#FDFBF7] p-10 md:p-16 relative overflow-hidden gold-glow">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C9A961]/25 blur-3xl animate-drift" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#556B57]/20 blur-3xl animate-drift" style={{ animationDelay: "6s" }} />
          <div className="absolute top-10 right-16 w-40 h-40 rounded-full border gold-border animate-spin-slow" />
          <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C9A961] text-[#1D3A2F] text-[10px] tracking-[0.22em] uppercase font-bold mb-6 animate-glow-pulse">
                <Sparkles className="w-3 h-3" /> First Ever in India · Completed
              </div>
              <h2 className="font-editorial text-5xl lg:text-7xl leading-[0.98]">
                {upcoming.name} ·
                <br />
                <em className="gold-text">
                  {upcoming.tagline?.split("—")[0]?.trim() || upcoming.tagline}
                </em>
              </h2>
              <p className="mt-6 text-lg text-[#FDFBF7]/85 max-w-xl leading-relaxed">
                Two days of hands-on lab workstations, active encapsulation
                demos, live product innovation and 1:1 brand consultations —{" "}
                <b className="text-[#FDFBF7]">
                  everything to launch your cosmetic brand in 72 hours.
                </b>
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {[
                  "🇮🇳 Never Before Done",
                  "600+ Formulations Live",
                  "1:1 Founder Consults",
                  "Hands-On Lab Sessions",
                  "3 Patent-Pending Textures",
                ].map((chip) => (
                  <span
                    key={chip}
                    className="px-3 py-1.5 rounded-full border border-[#FDFBF7]/25 text-[11px] tracking-[0.14em] uppercase text-[#FDFBF7]/85"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="bg-[#FDFBF7]/10 border gold-border rounded-2xl p-8 text-center backdrop-blur-sm">
                <div className="eyebrow text-[#FDFBF7]/60 mb-2">
                  Registration Fee
                </div>
                <div className="font-editorial text-6xl gold-text">
                  ₹{(upcoming.price_inr || 0).toLocaleString("en-IN")}
                </div>
                <div className="text-xs text-[#FDFBF7]/60 mt-3">
                  All-Inclusive 2-Day Pass
                </div>
                <div className="mt-5 pt-5 border-t border-[#FDFBF7]/15 text-left space-y-2">
                  <div className="flex items-center gap-2 text-sm text-[#FDFBF7]/85">
                    <Calendar className="w-4 h-4 text-[#C9A961]" />{" "}
                    {upcoming.date}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#FDFBF7]/85">
                    <MapPin className="w-4 h-4 text-[#C9A961]" />{" "}
                    {upcoming.location}
                  </div>
                </div>
                <div className="mt-6 flex flex-col gap-2 items-center">
                  <span data-testid="banner-completed-tag" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#FDFBF7] text-[#1D3A2F] text-[11px] tracking-[0.18em] uppercase font-bold">
                    <Check className="w-4 h-4" /> Event Completed
                  </span>
                  <a
                    href="https://www.pliexpo.com"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="banner-pliexpo-link"
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-full border border-[#FDFBF7]/30 text-[#FDFBF7] text-[10px] tracking-[0.18em] uppercase hover:border-[#C9A961] transition-colors"
                  >
                    Next edition updates <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ─── WHY ATTEND ─── */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-4">Why attend</div>
            <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.02]">
              Six reasons this is the
              <br />
              <em className="gold-text">most important 48 hours</em>
              <br />
              of your brand year.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-lg text-[#222222]/70 leading-relaxed">
              Whether you're pre-launch, scaling, or preparing to export —
              PLI 3.0 compresses the knowledge, connections and confidence
              usually reserved for industry insiders into a single virtual
              stage.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              icon: Rocket,
              t: "Live 72-hr Brand Build",
              d: "Watch a real cosmetic brand go from blank canvas to shippable product across 48 hours of live-streamed R&D, packaging and dispatch demos.",
            },
            {
              icon: Users,
              t: "1,000+ Founder Network",
              d: "Curated founder rooms, category-specific breakouts and one-on-one matchmaking with distributors from 15 export destinations.",
            },
            {
              icon: Beaker,
              t: "R&D Deep-Dives",
              d: "Live sessions on 2026's most-searched actives — PDRN, Exosomes, NAD+, Peptides — from Fresio's 18-chemist formulation lab.",
            },
            {
              icon: Award,
              t: "Founder Awards Night",
              d: "Recognition ceremony for standout brands launched with Fresio in 2025-26. Nominate yourself before entry closes on 15 Aug.",
            },
            {
              icon: Globe,
              t: "Global Export Lounge",
              d: "Live Q&A with buyers from UAE, USA, UK, Singapore, Kenya, Australia — the countries pulling the most Indian beauty imports right now.",
            },
            {
              icon: PlayCircle,
              t: "Lifetime Replay Access",
              d: "Every session recorded and available for 12 months. Miss nothing, revisit everything, share with your team on-demand.",
            },
          ].map(({ icon: Icon, t, d }) => (
            <div
              key={t}
              className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-7 hover-lift"
            >
              <div className="w-11 h-11 rounded-full bg-[#F7F4EE] flex items-center justify-center mb-5 border border-[#D7D4CF]">
                <Icon
                  className="w-5 h-5 text-[#1D3A2F]"
                  strokeWidth={1.4}
                />
              </div>
              <h3 className="font-editorial text-2xl text-[#1D3A2F] leading-tight">
                {t}
              </h3>
              <p className="text-sm text-[#222222]/70 mt-3 leading-relaxed">
                {d}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── WHAT'S INCLUDED ─── */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
        <div className="rounded-3xl bg-[#F7F4EE] border border-[#D7D4CF]/60 p-10 md:p-14">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">
                Your ₹4,999 ticket includes
              </div>
              <h3 className="font-editorial text-4xl text-[#1D3A2F] leading-[1.05]">
                Everything you need
                <br />
                <em className="gold-text">for two days.</em>
              </h3>
              <p className="mt-5 text-sm text-[#556B57] leading-relaxed max-w-sm">
                One flat fee. No upsells. No pay-per-session. What you see is
                what you get.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {[
                  "2 full days of live-stream access",
                  "12 keynotes + 8 workshops",
                  "Live Q&A with Fresio chemists",
                  "Global buyer matchmaking room",
                  "Founder-of-the-year awards ceremony",
                  "Digital sample kit shipped home",
                  "12-month session replay library",
                  "Discount voucher for PLI 4.0",
                  "Exclusive Fresio catalog preview",
                  "Certificate of Attendance",
                ].map((line) => (
                  <li
                    key={line}
                    className="flex items-start gap-3 text-[#1D3A2F]"
                  >
                    <Check className="w-4 h-4 text-[#556B57] mt-1 flex-shrink-0" />
                    <span className="text-sm">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TIMELINE / CARDS ─── */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
        <div className="eyebrow mb-4">Editions</div>
        <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.05] mb-12">
          Three years,
          <br />
          <em className="gold-text">one growing community.</em>
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {events.map((ev) => (
            <EventCard key={ev.id} ev={ev} />
          ))}
        </div>
      </section>

      {/* ─── UPCOMING SPOTLIGHT ─── */}
      {upcoming && (
        <section
          id="pli-register"
          className="max-w-[1400px] mx-auto px-6 lg:px-10 scroll-mt-28"
        >
          <div className="rounded-3xl bg-[#1D3A2F] text-[#FDFBF7] overflow-hidden relative">
            <div className="absolute top-10 right-10 w-40 h-40 rounded-full border border-[#FDFBF7]/10 animate-spin-slow" />
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-[#FDFBF7]/5 blur-2xl" />
            <div className="grid lg:grid-cols-12 gap-10 p-10 md:p-16 relative z-10">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full bg-[#FDFBF7] text-[#1D3A2F] text-[10px] tracking-[0.18em] uppercase font-semibold">
                  <Check className="w-3 h-3" /> Completed Edition
                </div>
                <h2 className="font-editorial text-5xl md:text-7xl leading-[0.95]">
                  {upcoming.name}
                </h2>
                <p className="font-editorial text-2xl text-[#D7D4CF] italic mt-3">
                  {upcoming.tagline}
                </p>

                <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
                  <InfoBlock
                    icon={Calendar}
                    label="Date"
                    value={upcoming.date}
                  />
                  <InfoBlock
                    icon={MapPin}
                    label="Format"
                    value={upcoming.location}
                  />
                  <InfoBlock
                    icon={Ticket}
                    label="Ticket"
                    value={
                      upcoming.price_inr
                        ? `₹${upcoming.price_inr.toLocaleString("en-IN")}`
                        : "Free"
                    }
                  />
                </div>

                <div className="mt-10">
                  <div className="eyebrow text-[#FDFBF7]/60 mb-4">
                    Event Highlights
                  </div>
                  <ul className="space-y-3">
                    {upcoming.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-3 text-[#FDFBF7]/85">
                        <Check className="w-4 h-4 text-[#D7D4CF] mt-0.5 flex-shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div data-testid="pli-completed-card" className="h-full rounded-3xl bg-[#FDFBF7]/[0.06] border gold-border p-10 flex flex-col justify-center items-start">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full btn-gold text-[11px] tracking-[0.2em] uppercase font-bold">
                    <Check className="w-4 h-4" /> Completed
                  </span>
                  <h3 className="font-editorial text-4xl md:text-5xl mt-8 leading-[1.05]">
                    Thank you to every founder <em className="gold-text">who joined us.</em>
                  </h3>
                  <p className="mt-5 text-[#FDFBF7]/75 leading-relaxed max-w-md">
                    {upcoming.name} has concluded. Registrations are now closed — follow pliexpo.com for announcements on the next edition.
                  </p>
                  <a
                    href="https://www.pliexpo.com"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="pli-next-edition-link"
                    className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full border border-[#FDFBF7]/30 text-[11px] tracking-[0.18em] uppercase hover:border-[#C9A961] transition-colors"
                  >
                    Visit pliexpo.com <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── PLI 3.0 FORMULATIONS + GALLERY (rich event) ─── */}
      {upcoming?.formulations && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C9A961]/15 border gold-border mb-4">
                <span className="text-[10px] tracking-[0.22em] uppercase gold-text font-bold">
                  Three Advanced Formulations
                </span>
              </div>
              <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.05]">
                Formulate, test, inspect
                <br />
                <em className="gold-text">these patent-pending textures.</em>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-[#222222]/70 leading-relaxed">
                Three flagship innovations developed inside Fresio's R&D lab —
                each with a live hands-on synthesis or clinical review across
                the 2-day masterclass.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {upcoming.formulations.map((f, i) => (
              <div
                key={f.name}
                className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-8 hover-lift relative overflow-hidden"
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#C9A961]/8 blur-2xl" />
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[10px] tracking-[0.22em] uppercase gold-text font-bold">
                      Concept 0{i + 1}
                    </span>
                    <span className="text-[10px] tracking-[0.18em] uppercase text-[#556B57] font-semibold">
                      {f.tag}
                    </span>
                  </div>
                  <h3 className="font-editorial text-3xl text-[#1D3A2F] leading-tight">
                    {f.name}
                  </h3>
                  <p className="text-sm text-[#222222]/70 mt-4 leading-relaxed">
                    {f.d}
                  </p>
                  <div className="mt-6 pt-4 border-t border-[#D7D4CF]/60 text-xs tracking-[0.14em] uppercase text-[#1D3A2F] font-semibold">
                    {f.when}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ─── VENUE + AGENDA ─── */}
      {upcoming?.venue && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <div className="rounded-3xl bg-gradient-to-br from-[#1D3A2F] via-[#2A4E3F] to-[#0F251C] text-[#FDFBF7] p-10 md:p-14 relative overflow-hidden gold-glow">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C9A961]/20 blur-3xl animate-drift" />
            <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#556B57]/15 blur-3xl animate-drift" style={{ animationDelay: "6s" }} />
            <div className="relative z-10 grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <div className="eyebrow text-[#FDFBF7]/60 mb-4">Physical Venue</div>
                <h3 className="font-editorial text-4xl leading-[1.05]">
                  <em className="gold-text">Oneiros Sports Club</em>
                  <br />
                  Adajan, Surat.
                </h3>
                <p className="mt-6 text-sm text-[#FDFBF7]/75 leading-relaxed max-w-md">
                  A landmark hospitality address with dedicated hands-on lab
                  workstations, 1:1 breakout rooms, and a curated founders'
                  lounge. Strictly limited capacity for lab safety.
                </p>
                <a
                  href={upcoming.venue_map}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C9A961] text-[#1D3A2F] text-[11px] tracking-[0.18em] uppercase font-bold hover:bg-[#E6D5A4] transition"
                >
                  <MapPin className="w-3.5 h-3.5" /> View on Google Maps
                </a>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { d: "Day 01 · Sat 11 Oct", h: "09:30 – 18:30", items: ["Registration & Welcome Kit", "Opening — Future of Product Innovation", "Innovation — 3D Capsule Cream", "Networking Founder Lunch", "Innovation — Dual-Phase Body Lotion", "Product Innovation Workshop", "Innovation — PDRN Serum", "1:1 Brand Discovery"] },
                  { d: "Day 02 · Sun 12 Oct", h: "09:30 – 18:30", items: ["Day 02 Opening — Idea to Product", "Product Development Masterclass", "Portfolio & Next-SKU Workshop", "Executive Lunch & Networking", "1:1 Expert Consultations", "Product Direction Session", "Commercial & Execution Discussion", "Founder Closing Session"] },
                ].map((day) => (
                  <div key={day.d} className="rounded-2xl bg-[#FDFBF7]/5 border border-[#FDFBF7]/10 p-5">
                    <div className="eyebrow text-[#FDFBF7]/60 mb-1">{day.d}</div>
                    <div className="text-xs text-[#C9A961] font-semibold mb-4">{day.h}</div>
                    <ul className="space-y-2">
                      {day.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 text-sm text-[#FDFBF7]/85">
                          <span className="w-1 h-1 rounded-full bg-[#C9A961] mt-2 flex-shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── PLI 3.0 GALLERY ─── */}
      {upcoming?.gallery && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <div className="grid lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-6">
              <div className="eyebrow mb-4">Gallery · Behind the Lab Glass</div>
              <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.05]">
                Candid moments,
                <br />
                <em className="gold-text">hands-on formulations.</em>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-[#222222]/70 leading-relaxed">
                A visual chronicle of PLI — the energy of innovation in motion,
                captured from previous editions.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {upcoming.gallery.map((src, i) => (
              <div
                key={src}
                className={`relative overflow-hidden rounded-2xl group ${
                  i % 5 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"
                }`}
              >
                <img
                  src={src}
                  alt={`PLI 3.0 gallery ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1D3A2F]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ─── PAST GALLERY ─── */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24">
        <div className="eyebrow mb-4">Past editions</div>
        <h3 className="font-editorial text-4xl text-[#1D3A2F] mb-10">
          Moments from PLI.
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {events
            .filter((e) => e.status === "done")
            .map((ev) => (
              <div key={ev.id} className="group relative overflow-hidden rounded-3xl aspect-[4/5]">
                <img
                  src={ev.image}
                  alt={ev.name}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1D3A2F]/90 via-transparent to-transparent" />
                <div className="absolute inset-x-6 bottom-6 text-[#FDFBF7]">
                  <div className="eyebrow text-[#FDFBF7]/70">{ev.date}</div>
                  <div className="font-editorial text-3xl mt-1">{ev.name}</div>
                  <div className="text-sm text-[#FDFBF7]/70">
                    {ev.location}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}

function Metric({ k, v }) {
  return (
    <div className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-8">
      <div className="font-editorial text-4xl text-[#1D3A2F]">{k}</div>
      <div className="text-xs tracking-[0.18em] uppercase text-[#556B57] mt-3">
        {v}
      </div>
    </div>
  );
}

function InfoBlock({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[#FDFBF7]/60 text-[11px] tracking-[0.22em] uppercase mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-editorial text-2xl text-[#FDFBF7]">{value}</div>
    </div>
  );
}

function EventCard({ ev }) {
  const isDone = ev.status === "done";
  return (
    <div
      data-testid={`pli-card-${ev.id}`}
      className="relative overflow-hidden rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 hover-lift"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={ev.image}
          alt={ev.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1D3A2F]/60 via-transparent to-transparent" />
        <div className="absolute top-5 left-5">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase font-semibold ${
              isDone
                ? "bg-[#FDFBF7]/85 text-[#1D3A2F]"
                : "bg-[#1D3A2F] text-[#FDFBF7]"
            }`}
          >
            {isDone ? <Check className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
            {isDone ? "Completed" : "Upcoming"}
          </span>
        </div>
      </div>
      <div className="p-7">
        <div className="font-editorial text-3xl text-[#1D3A2F]">{ev.name}</div>
        <p className="text-sm text-[#556B57] italic mt-1">{ev.tagline}</p>
        <div className="mt-5 space-y-2 text-sm text-[#222222]/75">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-[#556B57]" /> {ev.date}
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-[#556B57]" /> {ev.location}
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-3.5 h-3.5 text-[#556B57]" /> {ev.highlights[0]}
          </div>
        </div>
      </div>
    </div>
  );
}

function RegistrationForm({ events, defaultId }) {
  const [eventId, setEventId] = useState(defaultId);
  const current = events.find((e) => e.id === eventId) || events[0];
  const eventName = current.name;
  const priceInr = current.price_inr || 0;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    city: "",
    attendees: 1,
    interest: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const openRazorpay = (reg) =>
    new Promise((resolve) => {
      const options = {
        key: reg.razorpay_key_id,
        amount: reg.amount_paise,
        currency: "INR",
        order_id: reg.razorpay_order_id,
        name: "Fresio Herbals",
        description: `${eventName} · Ticket`,
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone,
        },
        theme: { color: "#1D3A2F" },
        handler: async (res) => {
          try {
            await http.post("/pli-events/register/verify", {
              order_id: reg.id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success(
              `Payment confirmed! You're in for ${eventName}.`,
            );
            setDone(true);
            resolve(true);
          } catch {
            toast.error("Payment received but verification failed. Our team will reach out.");
            resolve(false);
          }
        },
        modal: {
          ondismiss: () => resolve(false),
        },
      };
      if (!window.Razorpay) {
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => new window.Razorpay(options).open();
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
      } else {
        new window.Razorpay(options).open();
      }
    });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: reg } = await http.post(
        `/pli-events/${eventId}/register`,
        form,
      );
      if (!reg.razorpay_order_id || reg.test_mode) {
        toast.success(`You're registered for ${eventName}.`);
        setDone(true);
        return;
      }
      await openRazorpay(reg);
    } catch {
      toast.error("Could not register. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-3xl bg-[#FDFBF7] text-[#1D3A2F] p-10 text-center h-full flex flex-col items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-[#1D3A2F] text-[#FDFBF7] flex items-center justify-center mb-6">
          <Check className="w-7 h-7" />
        </div>
        <h3 className="font-editorial text-3xl">Registration confirmed</h3>
        <p className="mt-3 text-sm text-[#556B57] max-w-xs">
          A confirmation with agenda, live-stream link and speaker line-up will
          hit your inbox soon.
        </p>
      </div>
    );
  }

  const priceLabel = priceInr
    ? `₹${priceInr.toLocaleString("en-IN")} / seat`
    : "Free";

  return (
    <form
      onSubmit={submit}
      data-testid="pli-registration-form"
      className="rounded-3xl bg-[#FDFBF7] text-[#1D3A2F] p-8 md:p-10 space-y-4"
    >
      <a
        href="https://www.pliexpo.com"
        target="_blank"
        rel="noreferrer"
        data-testid="pli-register-external"
        className="flex items-center justify-between gap-3 rounded-2xl bg-[#1D3A2F] text-[#F4E4BD] px-5 py-4 mb-2 hover:bg-black transition-colors"
      >
        <span className="text-sm"><b className="font-semibold">Register on www.pliexpo.com</b><span className="block text-xs text-[#FDFBF7]/60">Official PLI registration portal</span></span>
        <ArrowUpRight className="w-5 h-5 shrink-0" />
      </a>
      <div className="text-center text-[10px] tracking-[0.2em] uppercase text-[#556B57]">or pay & register here</div>
      <div className="flex flex-wrap gap-2" data-testid="pli-event-switch">
        {events.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={() => setEventId(e.id)}
            data-testid={`pli-event-option-${e.id}`}
            className={`h-10 px-4 rounded-full text-xs border transition-colors ${e.id === current.id ? "bg-[#1D3A2F] text-[#F4E4BD] border-[#1D3A2F]" : "border-[#D7D4CF] hover:border-[#C9A961]"}`}
          >
            {e.name} · ₹{(e.price_inr || 0).toLocaleString("en-IN")}
          </button>
        ))}
      </div>
      <div className="eyebrow">Register — {priceLabel}</div>
      <h3 className="font-editorial text-3xl leading-[1.05]">
        Reserve your seat
        <br />
        for {eventName}.
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4">
        <FormInput
          label="Full name"
          k="name"
          required
          value={form.name}
          set={set}
        />
        <FormInput
          label="Email"
          k="email"
          type="email"
          required
          value={form.email}
          set={set}
        />
        <FormInput
          label="Phone"
          k="phone"
          type="tel"
          required
          value={form.phone}
          set={set}
        />
        <FormInput
          label="Company / Brand"
          k="company"
          value={form.company}
          set={set}
        />
        <FormInput label="Role" k="role" value={form.role} set={set} />
        <FormInput label="City" k="city" value={form.city} set={set} />
        <div>
          <label className="eyebrow block mb-2">Attendees</label>
          <input
            data-testid="pli-attendees"
            type="number"
            min={1}
            max={20}
            value={form.attendees}
            onChange={(e) => set("attendees", parseInt(e.target.value || "1"))}
            className="w-full h-12 px-5 rounded-full bg-[#F7F4EE] border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#1D3A2F]"
          />
        </div>
        <FormInput
          label="Interest area"
          k="interest"
          placeholder="Face care · OEM · Export…"
          value={form.interest}
          set={set}
        />
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid="pli-register-submit"
        className="mt-2 inline-flex items-center gap-3 h-13 py-4 px-7 rounded-full bg-[#1D3A2F] text-[#FDFBF7] text-[12px] tracking-[0.18em] uppercase font-medium hover:bg-black transition disabled:opacity-70"
      >
        {busy
          ? "Processing…"
          : priceInr > 0
            ? `Pay & Register — ₹${priceInr.toLocaleString("en-IN")}`
            : "Register Now"}{" "}
        <ArrowRight className="w-4 h-4" />
      </button>
      <p className="text-[10px] tracking-[0.16em] uppercase text-[#556B57] pt-2">
        {priceInr > 0
          ? "Secure Razorpay payment · Instant confirmation · Live-stream access"
          : "Free entry · Limited seats · Confirmation via email"}
      </p>
    </form>
  );
}

function FormInput({ label, k, type = "text", required, value, set, placeholder }) {
  return (
    <div>
      <label className="eyebrow block mb-2">
        {label} {required && <span className="text-[#556B57]">*</span>}
      </label>
      <input
        data-testid={`pli-${k}`}
        type={type}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={(e) => set(k, e.target.value)}
        className="w-full h-12 px-5 rounded-full bg-[#F7F4EE] border border-[#D7D4CF] text-sm focus:outline-none focus:border-[#1D3A2F]"
      />
    </div>
  );
}
